import React from 'react';
import { GetServerSideProps, GetServerSidePropsResult } from 'next';
import Head from 'next/head';
import { RoutineLinkPreviewMetadataResponse } from 'hevy-shared';
import { RoutineDetailScreen } from 'screens/RoutineDetail/RoutineDetailScreen';
import API from 'utils/ServerSideAPI';
import cookie from 'cookie';
import { BootstrapLoadingWrapper } from 'components/BootstrapLoadingWrapper';
import { t } from 'translations/t';

import { BrowserLanguageProps } from 'utils/types';
import { browserLanguageFromContext } from 'utils/pageUtils';

type RoutineProps = {
  urlRoutineId: string | null;
  metadata: RoutineLinkPreviewMetadataResponse | null;
  isAuthenticated: boolean;
} & BrowserLanguageProps;

export const getServerSideProps: GetServerSideProps = async (
  context,
): Promise<GetServerSidePropsResult<RoutineProps>> => {
  const { shortId: urlRoutineId } = context.query;
  const rawCookies = context.req?.headers.cookie;
  const cookies = rawCookies ? cookie.parse(rawCookies) : {};

  const isAuthenticated = API.isAuthenticated(cookies);

  if (typeof urlRoutineId !== 'string') {
    return {
      props: {
        urlRoutineId: null,
        metadata: null,
        isAuthenticated,
        ...browserLanguageFromContext(context),
      },
    };
  }

  try {
    const result = await API.getRoutinePreviewMetadata(urlRoutineId);

    return {
      props: {
        urlRoutineId,
        metadata: result.data,
        isAuthenticated,
        ...browserLanguageFromContext(context),
      },
    };
  } catch {
    return {
      props: {
        urlRoutineId,
        metadata: null,
        isAuthenticated,
        ...browserLanguageFromContext(context),
      },
    };
  }
};

export default function RoutineDetail({
  urlRoutineId,
  metadata,
  isAuthenticated,
  browserLanguage,
}: RoutineProps) {
  return (
    <>
      {metadata && (
        <Head>
          <title>{metadata.title}</title>
          <meta
            name="description"
            content={t('web.metadata.createdByUser', {
              username: metadata.username,
            })}
          />
          <meta
            name="robots"
            content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
          />
          <link rel="canonical" href={metadata.url} />
          <meta property="og:locale" content={browserLanguage} />
          <meta property="og:locale:alternate" content="ar" />
          <meta property="og:locale:alternate" content="ca" />
          <meta property="og:locale:alternate" content="de" />
          <meta property="og:locale:alternate" content="en" />
          <meta property="og:locale:alternate" content="es" />
          <meta property="og:locale:alternate" content="fr" />
          <meta property="og:locale:alternate" content="hi" />
          <meta property="og:locale:alternate" content="it" />
          <meta property="og:locale:alternate" content="ja" />
          <meta property="og:locale:alternate" content="ko" />
          <meta property="og:locale:alternate" content="pl" />
          <meta property="og:locale:alternate" content="pt_BR" />
          <meta property="og:locale:alternate" content="pt_PT" />
          <meta property="og:locale:alternate" content="ru" />
          <meta property="og:locale:alternate" content="tr" />
          <meta property="og:locale:alternate" content="zh_CN" />
          <meta property="og:locale:alternate" content="zh_TW" />
          <meta name="og:type" content="website" />
          <meta property="og:title" content={metadata.title} />
          <meta
            property="og:description"
            content={t('web.metadata.createdByUser', {
              username: metadata.username,
            })}
          />
          <meta property="og:url" content={metadata.url} />
          <meta property="og:site_name" content={t('web.metadata.socialWorkoutTracker')} />
          <meta property="og:image" content={metadata.image_url} />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:creator" content="@hevyapp" />
          <meta name="twitter:site" content="@hevyapp" />
          <meta name="twitter:title" content={metadata.title} />
          <meta
            name="twitter:description"
            content={t('web.metadata.createdByUser', {
              username: metadata.username,
            })}
          />
          <meta name="twitter:image" content={metadata.twitter_image_url} />
          <meta name="twitter:label1" content={t('web.metadata.createdBy')} />
          <meta name="twitter:data1" content={metadata.username} />
          <meta name="twitter:label2" content={t('web.metadata.numExercises')} />
          <meta name="twitter:data2" content={`${metadata.exercise_count}`} />
        </Head>
      )}
      <BootstrapLoadingWrapper>
        <RoutineDetailScreen routineId={urlRoutineId} isAuthenticated={isAuthenticated} />
      </BootstrapLoadingWrapper>
    </>
  );
}
